import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", jurisdiction: "ON", category: "provincial_funding", plain_language_summary: "",
  who_may_review: "", eligibility_notes: "", common_documents_needed: "", application_url: "",
  official_source_url: "", disclaimer: "", tags: "", active: true };

export default function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/programs"); setPrograms(data); setLoading(false); };
  useEffect(() => { load(); }, []);
  if (loading) return <Spinner />;

  const openEdit = (p) => setEditing(p ? {
    ...p, common_documents_needed: (p.common_documents_needed || []).join(", "), tags: (p.tags || []).join(", "),
  } : { ...EMPTY });

  return (
    <div>
      <PageHeader eyebrow="Content" title="Program Content Manager" subtitle="Create and update funding & support program content without a redeploy."
        action={<button onClick={() => openEdit(null)} className="kid-btn kid-btn-primary h-11 px-5" data-testid="admin-program-new"><Plus size={16} /> New program</button>} />
      <div className="kid-card overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-programs-table">
          <thead className="bg-kid-mist text-kid-muted text-left"><tr>
            <th className="px-5 py-3 font-poppins font-semibold">Program</th><th className="px-5 py-3">Region</th>
            <th className="px-5 py-3">Category</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr></thead>
          <tbody>
            {programs.map((p) => (
              <tr key={p.id} className="border-t border-kid-line" data-testid="admin-program-row">
                <td className="px-5 py-3 font-medium text-kid-navy">{p.name}</td>
                <td className="px-5 py-3">{p.jurisdiction}</td>
                <td className="px-5 py-3">{p.category.replace(/_/g, " ")}</td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.active ? "bg-kid-green/15 text-[#3f9d5c]" : "bg-kid-mist text-kid-muted"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                <td className="px-5 py-3 text-right"><button onClick={() => openEdit(p)} className="text-kid-teal font-semibold inline-flex items-center gap-1" data-testid="admin-program-edit"><Pencil size={14} /> Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ProgramModal program={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProgramModal({ program, onClose, onDone }) {
  const [form, setForm] = useState(program);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setError("");
    if (!form.name || !form.official_source_url || !form.plain_language_summary) { setError("Name, summary, and official source URL are required."); return; }
    const payload = { ...form,
      common_documents_needed: form.common_documents_needed ? form.common_documents_needed.split(",").map((s) => s.trim()).filter(Boolean) : [],
      tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    setBusy(true);
    try {
      if (form.id) await api.patch(`/admin/programs/${form.id}`, payload);
      else await api.post("/admin/programs", payload);
      toast.success("Program saved."); onDone();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-program-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit program" : "New program"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {error && <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="admin-program-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Jurisdiction</Label><Input value={form.jurisdiction} onChange={set("jurisdiction")} /></div>
              <div className="space-y-1"><Label>Category</Label><Input value={form.category} onChange={set("category")} /></div>
            </div>
          </div>
          <div className="space-y-1"><Label>Plain-language summary</Label><textarea rows={2} value={form.plain_language_summary} onChange={set("plain_language_summary")} className="w-full rounded-xl border border-kid-line p-3 text-sm" /></div>
          <div className="space-y-1"><Label>Who may want to review</Label><textarea rows={2} value={form.who_may_review || ""} onChange={set("who_may_review")} className="w-full rounded-xl border border-kid-line p-3 text-sm" /></div>
          <div className="space-y-1"><Label>Common documents (comma separated)</Label><Input value={form.common_documents_needed} onChange={set("common_documents_needed")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Application URL</Label><Input value={form.application_url || ""} onChange={set("application_url")} /></div>
            <div className="space-y-1"><Label>Official source URL *</Label><Input value={form.official_source_url} onChange={set("official_source_url")} data-testid="admin-program-source" /></div>
          </div>
          <div className="space-y-1"><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={set("tags")} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="kid-btn kid-btn-ghost h-11 px-5">Cancel</button>
          <button onClick={submit} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5" data-testid="admin-program-save">{busy ? "Saving…" : "Save program"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
